import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { boardStateAtom, presentationElementIdAtom, presentationElementsAtom, presentationModeAtom, roleAtom, } from '@/store/boardAtoms';
import { getCachedImage } from '@/utils/board/imageCacheUtils';
import { isRectIntersecting } from '@/utils/board/zIndexHelpers';
import { getElementBounds } from '@/utils/presentation';
/**
 * Хук для предзагрузки изображений всех фреймов презентации.
 * Предзагружает изображения для зрителей, чтобы избежать задержек при переключении слайдов.
 */
export const usePreloadPresentationImages = () => {
    const [boardState] = useAtom(boardStateAtom);
    const [presentationElements] = useAtom(presentationElementsAtom);
    const [presentationMode] = useAtom(presentationModeAtom);
    const [role] = useAtom(roleAtom);
    const [presentationElementId] = useAtom(presentationElementIdAtom);
    // Собираем URL изображений, разделяя на приоритетные (активный фрейм) и остальные
    const { priorityUrls, otherUrls } = useMemo(() => {
        if (!presentationMode || role !== 'VIEWER' || presentationElements.length === 0) {
            return { priorityUrls: [], otherUrls: [] };
        }
        const prioritySeen = new Set();
        const allSeen = new Set();
        const priorityUrls = [];
        const otherUrls = [];
        // Собирает URL изображений для фрейма
        const collectUrlsForFrame = (frame, isPriority) => {
            var _a, _b;
            if (!frame)
                return;
            const frameBounds = getElementBounds(frame);
            const seen = isPriority ? prioritySeen : allSeen;
            const urls = isPriority ? priorityUrls : otherUrls;
            // URL самого фрейма
            if (((_a = frame.asset) === null || _a === void 0 ? void 0 : _a.url) && !seen.has(frame.asset.url)) {
                seen.add(frame.asset.url);
                urls.push(frame.asset.url);
                if (!isPriority)
                    prioritySeen.add(frame.asset.url);
            }
            // URL элементов, пересекающихся с фреймом
            for (const layer of boardState.layers || []) {
                if (!layer.visible)
                    continue;
                for (const element of layer.elements || []) {
                    if (element.id === frame.id)
                        continue;
                    if ((element.elementType === 'image' || element.elementType === 'token') &&
                        ((_b = element.asset) === null || _b === void 0 ? void 0 : _b.url) &&
                        !seen.has(element.asset.url) &&
                        isRectIntersecting(getElementBounds(element), frameBounds)) {
                        seen.add(element.asset.url);
                        urls.push(element.asset.url);
                        if (!isPriority)
                            prioritySeen.add(element.asset.url);
                    }
                }
            }
        };
        const presentationElementsMap = new Map(presentationElements.map((frame) => [frame.id, frame]));
        // Находим активный фрейм
        const activeFrame = presentationElementId != null
            ? presentationElementsMap.get(presentationElementId)
            : undefined;
        // Сначала собираем приоритетные (активный фрейм)
        collectUrlsForFrame(activeFrame, true);
        // Затем остальные
        for (const frame of presentationElements) {
            if (frame.id !== (activeFrame === null || activeFrame === void 0 ? void 0 : activeFrame.id)) {
                collectUrlsForFrame(frame, false);
            }
        }
        return { priorityUrls, otherUrls };
    }, [presentationMode, role, presentationElements, boardState.layers, presentationElementId]);
    // Отслеживаем уже загруженные URL, чтобы не перезагружать их при смене фрейма
    const loadedUrlsRef = useRef(new Set());
    // Предзагружаем изображения
    useEffect(() => {
        if (priorityUrls.length === 0 && otherUrls.length === 0)
            return;
        let cancelled = false;
        let cleanupIds = [];
        // Максимальное количество одновременных загрузок
        const MAX_CONCURRENT_LOADS = 3;
        // Размер батча для загрузки
        const BATCH_SIZE = 5;
        const preloadPriority = () => {
            // Фильтруем только новые URL, которые еще не загружены
            const newPriorityUrls = priorityUrls.filter((url) => !loadedUrlsRef.current.has(url));
            if (newPriorityUrls.length === 0) {
                // Если приоритетных нет, сразу запускаем загрузку остальных
                preloadOthers();
                return;
            }
            // Загружаем приоритетные изображения батчами с ограничением параллелизма
            let currentIndex = 0;
            const loadBatch = () => {
                if (cancelled)
                    return;
                const batch = newPriorityUrls.slice(currentIndex, currentIndex + BATCH_SIZE);
                if (batch.length === 0) {
                    // Все приоритетные загружены, запускаем остальные
                    preloadOthers();
                    return;
                }
                // Ограничиваем количество одновременных загрузок
                const activeLoads = batch.slice(0, MAX_CONCURRENT_LOADS);
                const remainingBatch = batch.slice(MAX_CONCURRENT_LOADS);
                Promise.allSettled(activeLoads.map((url) => getCachedImage(url)
                    .then((img) => {
                    if (img && !cancelled) {
                        loadedUrlsRef.current.add(url);
                    }
                })
                    .catch((error) => {
                    if (!cancelled) {
                        console.debug(`[Preload] Failed to preload priority image: ${url}`, error);
                    }
                }))).then(() => {
                    if (cancelled)
                        return;
                    currentIndex += activeLoads.length;
                    // Загружаем оставшиеся из батча, если есть
                    if (remainingBatch.length > 0) {
                        Promise.allSettled(remainingBatch.map((url) => getCachedImage(url)
                            .then((img) => {
                            if (img && !cancelled) {
                                loadedUrlsRef.current.add(url);
                            }
                        })
                            .catch((error) => {
                            if (!cancelled) {
                                console.debug(`[Preload] Failed to preload priority image: ${url}`, error);
                            }
                        }))).then(() => {
                            if (cancelled)
                                return;
                            // Используем requestIdleCallback для следующего батча, чтобы не блокировать UI
                            if ('requestIdleCallback' in window) {
                                const win = window;
                                const id = win.requestIdleCallback(loadBatch, { timeout: 50 });
                                cleanupIds.push(id);
                            }
                            else {
                                const id = setTimeout(loadBatch, 10);
                                cleanupIds.push(id);
                            }
                        });
                    }
                    else {
                        // Используем requestIdleCallback для следующего батча
                        if ('requestIdleCallback' in window) {
                            const win = window;
                            const id = win.requestIdleCallback(loadBatch, { timeout: 50 });
                            cleanupIds.push(id);
                        }
                        else {
                            const id = setTimeout(loadBatch, 10);
                            cleanupIds.push(id);
                        }
                    }
                });
            };
            // Запускаем первый батч через requestIdleCallback для неблокирующей загрузки
            if ('requestIdleCallback' in window) {
                const win = window;
                const id = win.requestIdleCallback(loadBatch, { timeout: 100 });
                cleanupIds.push(id);
            }
            else {
                const id = setTimeout(loadBatch, 10);
                cleanupIds.push(id);
            }
        };
        const preloadOthers = () => {
            if (cancelled)
                return;
            // Фильтруем только новые URL
            const newOtherUrls = otherUrls.filter((url) => !loadedUrlsRef.current.has(url));
            if (newOtherUrls.length === 0) {
                if (!cancelled) {
                    console.log(`[Preload] Preloaded ${priorityUrls.length} priority images`);
                }
                return;
            }
            // Загружаем остальные изображения батчами в свободное время
            let currentIndex = 0;
            const loadBatch = () => {
                if (cancelled)
                    return;
                const batch = newOtherUrls.slice(currentIndex, currentIndex + BATCH_SIZE);
                if (batch.length === 0) {
                    if (!cancelled) {
                        console.log(`[Preload] Preloaded ${priorityUrls.length} priority and ${newOtherUrls.length} other images`);
                    }
                    return;
                }
                Promise.allSettled(batch.map((url) => getCachedImage(url)
                    .then((img) => {
                    if (img && !cancelled) {
                        loadedUrlsRef.current.add(url);
                    }
                })
                    .catch((error) => {
                    if (!cancelled) {
                        console.debug(`[Preload] Failed to preload image: ${url}`, error);
                    }
                }))).then(() => {
                    if (cancelled)
                        return;
                    currentIndex += batch.length;
                    // Используем requestIdleCallback для следующего батча
                    if ('requestIdleCallback' in window) {
                        const win = window;
                        const id = win.requestIdleCallback(loadBatch, { timeout: 2000 });
                        cleanupIds.push(id);
                    }
                    else {
                        const id = setTimeout(loadBatch, 100);
                        cleanupIds.push(id);
                    }
                });
            };
            // Запускаем загрузку остальных изображений через requestIdleCallback
            if ('requestIdleCallback' in window) {
                const win = window;
                const id = win.requestIdleCallback(loadBatch, { timeout: 2000 });
                cleanupIds.push(id);
            }
            else {
                const id = setTimeout(loadBatch, 100);
                cleanupIds.push(id);
            }
        };
        preloadPriority();
        return () => {
            cancelled = true;
            cleanupIds.forEach((id) => {
                if (typeof id === 'number' && 'cancelIdleCallback' in window) {
                    window.cancelIdleCallback(id);
                }
                else {
                    clearTimeout(id);
                }
            });
            cleanupIds = [];
        };
    }, [priorityUrls, otherUrls]);
};
